import {
  getMarketingConsentPartners,
  getMarketingConsentServices,
  IMarketingConsentMarkets,
} from '@axo/form/configs';
import { useTranslation } from '@axo/shared/i18n';
import { Skeleton } from '@axo/ui-core/components/Skeleton';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../context/AxoForm/AxoForm';
import { PopupConsentUI } from '../../PopupConsent/PopupConsentUI';
import type { InnerConsentProps } from '../Consent';
import { useConsentConfig } from '../hooks/useConsentConfig';
import { MarketingField } from './MarketingField';

type PopupContent = {
  title: string;
  elements: string[];
};

export type ConsentPopupType = 'services' | 'partners';

export const Marketing = ({
  visible,
  applicant = 'main',
  external,
  terms,
}: InnerConsentProps & {
  applicant?: 'main' | 'coapp';
}) => {
  const { t } = useTranslation();

  const { data: consentConfig, loading } = useConsentConfig(!!external);

  const init = useRef(false);

  const [visiblePopup, setVisiblePopup] = useState<ConsentPopupType>();

  const { register, setValue } = useFormContext();
  const { marketCountry } = useAxoFormContext();

  const hidePopup = () => setVisiblePopup(undefined);
  const openPopup = (e: MouseEvent, type: ConsentPopupType) => {
    e.preventDefault();
    setVisiblePopup(type);
  };

  const popupContent: Record<string, PopupContent> = {
    services: {
      title: 'services',
      elements: consentConfig?.services
        ? consentConfig.services
        : getMarketingConsentServices(
            marketCountry.toLowerCase() as IMarketingConsentMarkets
          ),
    },
    partners: {
      title: 'partners',
      elements: consentConfig?.partners
        ? consentConfig.partners
        : getMarketingConsentPartners(
            marketCountry.toLowerCase() as IMarketingConsentMarkets
          ),
    },
  };

  useEffect(() => {
    if (!consentConfig) return;

    if (init.current) return;
    init.current = true;
    setValue('MarketingConsent/Config', consentConfig, {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [consentConfig, setValue]);

  if (!visible) return null;

  if (loading) return <Skeleton height={50} variant="dark" />;

  return (
    <>
      <input type="hidden" {...register('MarketingConsent/Config')} />

      <MarketingField
        applicant={applicant}
        consentConfig={consentConfig}
        openPopup={openPopup}
        terms={terms}
        external={external}
      />

      {visiblePopup && (
        <PopupConsentUI
          title={t(`Consent.marketing.${popupContent[visiblePopup].title}`)}
          partnersList={popupContent[visiblePopup].elements}
          buttonText={t('Consent.marketing.action')}
          onClick={hidePopup}
        />
      )}
    </>
  );
};
